import { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useActiveAgentPrompt } from '../../hooks/useAgents';

interface ActivePromptCardProps {
  agentId: string;
  onChangePrompt: () => void;
}

export function ActivePromptCard({ agentId, onChangePrompt }: ActivePromptCardProps) {
  const { data: activePrompt, isLoading, error } = useActiveAgentPrompt(agentId);
  const [isExpanded, setIsExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 p-6">
        <p className="text-sm text-red-600 dark:text-red-400">
          Error loading active prompt. Please try again.
        </p>
      </div>
    );
  }

  if (!activePrompt) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
          Active Prompt
        </h3>
        <div className="text-center py-8">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            No prompt assigned to this agent
          </p>
          <button
            type="button"
            onClick={onChangePrompt}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
          >
            Assign Prompt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          Active Prompt
        </h3>
        <button
          type="button"
          onClick={onChangePrompt}
          className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
        >
          Change Prompt
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
            {activePrompt.name}
          </h4>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronUpIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </button>
            <a
              href={`/prompts/${activePrompt.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
              title="Open prompt in new tab"
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
            </a>
          </div>
        </div>

        {isExpanded ? (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 max-h-64 overflow-y-auto">
            <pre className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap font-sans">
              {activePrompt.content}
            </pre>
          </div>
        ) : (
          <div className="relative">
            <div className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 whitespace-pre-wrap">
              {activePrompt.content}
            </div>
            {activePrompt.content && activePrompt.content.length > 150 && (
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-gray-800"></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
